import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Copy, Lock, MessageCircle, MessagesSquare, MoreHorizontal, Network, XCircle } from "lucide-react";

import { api, photoUri } from "@/lib/api";
import { useToast } from "@/components/toast";
import { usePaywall } from "@/components/paywall";
import { Modal } from "@/components/modal";
import { MemberActionsDialog } from "@/components/member-actions";
import { Button, EmptyState, ErrorState, Loading, PageHeader, Toggle } from "@/components/ui";

// The WhatsApp number is fetched on click from /members/{id}/contact and used only to open wa.me —
// it is never rendered, cached or stored.
async function openContact(memberId, text) {
  const tab = window.open("", "_blank");
  try {
    const res = await api(`/members/${memberId}/contact${text ? `?text=${encodeURIComponent(text)}` : ""}`);
    if (tab) { tab.opener = null; tab.location.href = res.wa_link; } else window.location.assign(res.wa_link);
  } catch (e) {
    tab?.close();
    throw e;
  }
}

function ConnectionItem({ c, onManage, onIce, opening, onOpen }) {
  const navigate = useNavigate();
  const { show: showPaywall } = usePaywall();
  const sub = "mt-3 flex h-12 w-full items-center justify-center gap-2 rounded-full border border-primary/30 bg-[#F6E3E4] text-sm font-bold text-primary transition-colors hover:bg-[#efd3d5]";
  return (
    <li className="fade-up rounded-[20px] border border-border bg-white p-4 shadow-sm" data-testid={`connection-item-${c.id}`}>
      <div className="flex items-center gap-3">
        <button data-testid={`connection-open-profile-${c.id}`} onClick={() => c.other && navigate(`/member/${c.other.id}`)} className="flex min-w-0 flex-1 items-center gap-3 text-left">
          <img src={photoUri(c.other?.main_photo_url)} alt="" className="h-16 w-16 shrink-0 rounded-full bg-[#F4EBE6] object-cover" />
          <span className="min-w-0">
            <span className="block truncate font-display text-[22px] leading-tight">{c.other?.display_name}, {c.other?.age}</span>
            <span className="block truncate text-sm text-muted-foreground">{c.other?.city}, {c.other?.country}</span>
            <span className="block text-xs font-bold text-primary">{c.type_label} · connected {new Date(c.created_at).toLocaleDateString()}</span>
            {!c.my_sharing ? <span className="block text-xs text-muted-foreground" data-testid={`connection-not-sharing-${c.id}`}>You stopped sharing your WhatsApp</span> : null}
          </span>
        </button>
        <button data-testid={`connection-manage-button-${c.id}`} aria-label="Manage connection" onClick={() => onManage(c)} className="flex h-10 w-10 items-center justify-center rounded-full hover:bg-[#F4EBE6]"><MoreHorizontal className="h-5 w-5" /></button>
      </div>
      {c.membership_locked ? (
        <button data-testid={`connection-premium-locked-${c.id}`} onClick={showPaywall} className={sub}><Lock className="h-[18px] w-[18px]" />Unlock WhatsApp & icebreakers with Premium</button>
      ) : null}
      {c.whatsapp_available && !c.membership_locked ? (
        <button data-testid={`connection-whatsapp-button-${c.id}`} disabled={opening === c.id} onClick={() => onOpen(c)}
          className="mt-4 flex h-12 w-full items-center justify-center gap-2 rounded-full bg-[#25D366] text-[15px] font-bold text-white transition-transform hover:brightness-95 active:scale-[0.98] disabled:opacity-70">
          <MessageCircle className="h-5 w-5" />{opening === c.id ? "Opening…" : "Connect on WhatsApp"}
        </button>
      ) : null}
      {c.whatsapp_available && c.icebreakers.length ? (
        <button data-testid={`connection-icebreakers-button-${c.id}`} onClick={() => onIce(c)} className={sub}><MessagesSquare className="h-[18px] w-[18px]" />Icebreakers</button>
      ) : null}
      {c.whatsapp_available ? null : (
        <p className="mt-3 flex items-center gap-2 rounded-xl bg-[#F4EBE6] p-3 text-sm text-muted-foreground" data-testid={`connection-whatsapp-unavailable-${c.id}`}>
          <Lock className="h-4 w-4" />This member has not enabled WhatsApp sharing.
        </p>
      )}
    </li>
  );
}

export default function Connections() {
  const toast = useToast();
  const qc = useQueryClient();
  const [opening, setOpening] = useState(null);
  const [manage, setManage] = useState(null);
  const [confirmDisconnect, setConfirmDisconnect] = useState(false);
  const [ice, setIce] = useState(null);
  const { data, isLoading, isError, error, refetch } = useQuery({ queryKey: ["connections"], queryFn: () => api("/connections") });
  const refresh = () => ["connections", "dashboard", "matches", "daily"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));

  const openWhatsApp = async (c) => {
    if (!c.other) return;
    setOpening(c.id);
    try { await openContact(c.other.id); } catch (e) { toast(e.message || "Could not open WhatsApp", "error"); } finally { setOpening(null); }
  };
  const sendIcebreaker = async (c, text) => {
    try { await openContact(c.other.id, text); setIce(null); } catch (e) { toast(e.message || "Could not open WhatsApp", "error"); }
  };
  const copyIcebreaker = async (text) => {
    try { await navigator.clipboard.writeText(text); toast("Copied — paste it in WhatsApp", "success"); } catch { toast("Could not copy", "error"); }
  };
  const setSharing = async (c, sharing) => {
    try {
      await api(`/connections/${c.id}/sharing`, { method: "PUT", body: { sharing } });
      setManage({ ...c, my_sharing: sharing });
      toast(sharing ? "You are sharing your WhatsApp again" : "You stopped sharing your WhatsApp here", "success");
      refresh();
    } catch (e) { toast(e.message, "error"); }
  };
  const disconnect = async (c) => {
    try {
      await api(`/connections/${c.id}/disconnect`, { method: "POST" });
      setManage(null);
      toast(`Disconnected from ${c.other?.display_name ?? "member"}`, "info");
      refresh();
    } catch (e) { toast(e.message, "error"); }
  };

  return (
    <div data-testid="connections-screen" className="mx-auto max-w-3xl">
      <PageHeader title="Connections" subtitle="Mutually accepted members" />
      {isLoading ? <Loading /> : isError ? <ErrorState testId="connections-error" message={error.message} onRetry={() => refetch()} /> : !data?.connections.length ? (
        <EmptyState testId="connections-empty" icon={Network} title="No connections yet." text="When a request is accepted, you can connect on WhatsApp from here." />
      ) : (
        <ul className="grid gap-3 md:grid-cols-2">{data.connections.map((c) => <ConnectionItem key={c.id} c={c} opening={opening} onOpen={openWhatsApp} onManage={setManage} onIce={setIce} />)}</ul>
      )}
      <Modal open={!!ice} onClose={() => setIce(null)} title="Icebreakers" testId="icebreakers-sheet">
        <p className="text-sm text-muted-foreground">Tap a line to open WhatsApp with it prefilled — you still press send yourself.</p>
        <ul className="mt-4 flex flex-col gap-2">
          {ice?.icebreakers.map((t, i) => (
            <li key={t} className="flex items-center gap-2 rounded-xl border border-border bg-[#FDFBF7] p-1 pl-4">
              <button data-testid={`icebreaker-send-${i}`} onClick={() => sendIcebreaker(ice, t)} className="flex-1 py-2 text-left text-sm hover:text-primary">{t}</button>
              <button data-testid={`icebreaker-copy-${i}`} aria-label="Copy icebreaker" onClick={() => copyIcebreaker(t)} className="flex h-10 w-10 items-center justify-center rounded-full text-primary hover:bg-[#F6E3E4]"><Copy className="h-[18px] w-[18px]" /></button>
            </li>
          ))}
        </ul>
      </Modal>
      <MemberActionsDialog member={manage?.other ?? null} onClose={() => { setManage(null); setConfirmDisconnect(false); }} extra={manage ? (
        <div className="flex flex-col gap-3">
          <div className="flex items-center gap-3 rounded-xl bg-[#F4EBE6] p-4">
            <span className="flex-1 text-sm font-bold">Share my WhatsApp with {manage.other?.display_name}</span>
            <Toggle testId="connection-sharing-switch" label="Share my WhatsApp" checked={manage.my_sharing} onChange={(v) => setSharing(manage, v)} />
          </div>
          <p className="text-xs text-muted-foreground" data-testid="connection-revoke-note">Revoking access cannot erase a number already copied or saved by the other person.</p>
          {confirmDisconnect ? (
            <div className="flex flex-col gap-2" data-testid="connection-disconnect-confirm">
              <p className="text-sm font-bold">Disconnect from {manage.other?.display_name}? You will both lose WhatsApp access.</p>
              <div className="flex gap-3">
                <Button testId="connection-disconnect-cancel" title="Keep" variant="outline" className="flex-1" onClick={() => setConfirmDisconnect(false)} />
                <Button testId="connection-disconnect-confirm-button" title="Disconnect" variant="danger" className="flex-1" onClick={() => { setConfirmDisconnect(false); disconnect(manage); }} />
              </div>
            </div>
          ) : <Button testId="connection-disconnect-button" title="Disconnect" variant="outline" icon={XCircle} onClick={() => setConfirmDisconnect(true)} />}
        </div>
      ) : null} />
    </div>
  );
}
